// Xerebo login DOM tweaks. Loaded on every website page via web_include_js; no-ops
// unless the login card is present. Only changes text that lives in the Jinja
// template (out of CSS's reach). Nothing in apps/frappe is modified.

(function () {
	// Ours, and the fallback for anything a tenant set that does not load.
	const DEFAULT_MARK = "/assets/xspace/images/xerebo-logo.png";

	// The panel mark reads the tenant's values from the :root block
	// update_website_context injects. There is no frappe.boot on a guest website page,
	// so computed custom properties are the channel that exists without adding a
	// request before first paint.
	//
	// Custom properties come back with their quotes; a name is stored quoted so a comma
	// or a brace in it cannot break the block.
	function read(name, fallback) {
		const v = (getComputedStyle(document.documentElement).getPropertyValue(name) || "").trim();
		return v.replace(/^["']|["']$/g, "").trim() || fallback;
	}

	// The two rings the page can draw. Their luma is written down rather than derived,
	// because these are the only two candidates and recomputing a constant every load
	// is work for nothing.
	const RING_LIGHT = { hex: "#ffffff", lum: 1 };
	const RING_DARK = { hex: "#14141a", lum: 0.077 };

	// Relative luma of any computed colour string.
	//
	// PAINTED AND READ BACK, not parsed. A regex over "rgb(...)" looked like enough and
	// is not: the login field is a color-mix(), which Chrome computes to
	// "oklch(0.884079 0.0165606 75.1395)" -- so the field measured as null and fell
	// back, while the panel beside it measured fine because a plain hex computes to
	// rgb(). Assigning to canvas fillStyle does NOT normalise it either; it hands the
	// oklch string straight back. Filling one pixel makes the browser rasterise it,
	// and the pixel is the answer whatever syntax it arrived in.
	function luma_of(colour) {
		if (!colour) return null;
		try {
			const c = document.createElement("canvas");
			c.width = c.height = 1;
			const ctx = c.getContext("2d");

			// An unparseable value leaves fillStyle untouched, so without a sentinel
			// every bad colour would silently measure as whatever was set before it --
			// black, and a confident wrong answer.
			ctx.fillStyle = "#010203";
			ctx.fillStyle = colour;
			if (ctx.fillStyle === "#010203") return null;

			ctx.fillRect(0, 0, 1, 1);
			const d = ctx.getImageData(0, 0, 1, 1).data;
			if (d[3] === 0) return null; // transparent: says nothing about what is behind

			return (0.2126 * d[0] + 0.7152 * d[1] + 0.0722 * d[2]) / 255;
		} catch (e) {
			return null;
		}
	}

	// THE RING HAS TO DIFFER FROM BOTH THE LOGO AND WHAT IS BEHIND IT.
	//
	// It sits between the two, so one comparison is not enough: matched to the
	// background the ring itself is invisible and the logo still bleeds into the panel,
	// and matched to the logo its edge is never found. Both failures look identical to
	// someone looking at the page -- "the logo is invisible" -- which is why measuring
	// only the logo was not sufficient.
	//
	// Each candidate is scored by its WORST separation of the two, and the better worst
	// case wins. An average would happily choose a ring that vanishes into the
	// background as long as it stood out from the logo.
	function pick_ring(logo_lum, bg_lum) {
		if (bg_lum === null || bg_lum === undefined) {
			// Nothing measurable behind it: fall back to opposing the logo alone,
			// which is still better than a fixed colour.
			return logo_lum < 0.5 ? RING_LIGHT : RING_DARK;
		}
		const score = function (c) {
			return Math.min(Math.abs(c.lum - logo_lum), Math.abs(c.lum - bg_lum));
		};
		return score(RING_LIGHT) >= score(RING_DARK) ? RING_LIGHT : RING_DARK;
	}

	// THE MARK'S STROKE COLOUR, measured from the logo AND from each surface it is
	// drawn on. Two surfaces exist and they are not the same colour: the panel (the
	// tenant's primary) carries the big mark, and the field (a
	// wash of their secondary) carries the mark above the form at narrow widths. One
	// ring colour cannot be right for both, so two are published.
	//
	// The logo is averaged over its OPAQUE pixels, weighted by alpha. A mark is mostly
	// transparent, and counting those pixels drags every logo toward the same mid grey
	// and makes the measurement worthless.
	function stroke_from(img) {
		let lum;
		try {
			const n = 24; // enough to characterise a mark, cheap to walk
			const c = document.createElement("canvas");
			c.width = c.height = n;
			const ctx = c.getContext("2d");
			ctx.drawImage(img, 0, 0, n, n);
			const d = ctx.getImageData(0, 0, n, n).data;

			let r = 0, g = 0, b = 0, a = 0;
			for (let i = 0; i < d.length; i += 4) {
				const alpha = d[i + 3];
				if (alpha < 32) continue; // effectively transparent
				r += d[i] * alpha;
				g += d[i + 1] * alpha;
				b += d[i + 2] * alpha;
				a += alpha;
			}
			if (!a) return; // nothing opaque to measure: keep the default ring

			// Rec. 709 luma, which is enough to answer "light or dark".
			lum = (0.2126 * (r / a) + 0.7152 * (g / a) + 0.0722 * (b / a)) / 255;
		} catch (e) {
			// A logo served from another origin taints the canvas and getImageData
			// throws. The default ring is already readable, so this stays silent
			// rather than trading a working page for a precise one.
			return;
		}

		// Read off the elements themselves rather than parsing --xs-fill and the wash:
		// both end up as color-mix() and var() chains, and the computed background is
		// already the resolved answer to what is actually behind the logo.
		const panel = luma_of(getComputedStyle(document.body, "::before").backgroundColor);
		const field = luma_of(getComputedStyle(document.body).backgroundColor);

		const root = document.documentElement.style;
		root.setProperty("--xs-login-mark-halo", pick_ring(lum, panel).hex);
		root.setProperty("--xs-login-field-halo", pick_ring(lum, field).hex);
	}

	// The big mark centred on the panel: the tenant's when they have one, ours when
	// they do not.
	function add_mark() {
		if (!document.querySelector("section.for-login, section.for-reset-password")) return;
		if (document.querySelector(".xs-login-mark")) return; // already added

		const wrap = document.createElement("div");
		wrap.className = "xs-login-mark";

		const img = document.createElement("img");
		// It USED to be decorative, with aria-hidden on the wrapper: the corner lockup
		// sat beside it and carried the brand name as text, so announcing it twice only
		// made the page longer to listen to. The lockup is gone, which leaves this as
		// the only thing on the page identifying whose login it is -- so it takes the
		// name it lost, and the wrapper drops aria-hidden with it.
		img.alt = read("--xs-login-name", "XEREBO");
		img.addEventListener("load", function () {
			stroke_from(img);
		});
		img.addEventListener("error", function () {
			if (img.src.indexOf(DEFAULT_MARK) === -1) img.src = DEFAULT_MARK;
		});
		img.src = read("--xs-login-logo", DEFAULT_MARK);

		wrap.appendChild(img);
		document.body.insertBefore(wrap, document.body.firstChild);
	}

	// A reset key means there is NO old password to compare against -- the client is
	// setting one for the first time. Core knows this and hides #old_password, but it
	// leaves the input in the DOM, and its keyup handler in frappe/www/update-password.html
	// still compares against it:
	//
	//     if (new_password.val() && old_password.val() === new_password.val()) { ...error... }
	//
	// Password managers fill hidden password inputs. When one puts the generated password
	// into #old_password too, new === old, the page shows "New password cannot be same as
	// old password", and the submit button never enables -- on a customer's very first
	// login, with nothing on screen explaining why. Emptying the field is enough to make
	// that comparison false again; disabling it stops the manager refilling it.
	//
	// Managers often fill AFTER load, so this keeps clearing for a short while rather
	// than running once. Value changes fire no mutation events, hence polling.
	function guard_old_password() {
		if (!document.querySelector("section.for-reset-password")) return;

		let key = "";
		try {
			key = new URLSearchParams(window.location.search).get("key") || "";
		} catch (e) {
			return; // no URLSearchParams: leave core's behaviour alone rather than guess
		}
		if (!key) return; // a signed-in user changing their password DOES have an old one

		const old = document.getElementById("old_password");
		if (!old) return;

		const clear = function () {
			if (old.value) old.value = "";
			old.disabled = true;
			old.setAttribute("autocomplete", "off");
		};

		clear();

		// Ten seconds covers a manager that fills on its own schedule; after that a
		// disabled, empty field is not going to acquire a value on its own.
		const until = Date.now() + 10000;
		const timer = setInterval(function () {
			clear();
			if (Date.now() > until) clearInterval(timer);
		}, 250);
	}

	function brand_login() {
		add_mark(); // also runs on the reset-password view, which shares the panel
		guard_old_password(); // reset-password view only; no-ops elsewhere

		const section = document.querySelector("section.for-login");
		if (!section) return; // not the login page

		// Heading + subtitle (template renders "Sign In" / "Welcome! Please sign in...").
		const head = section.querySelector(".page-card-head-text");
		if (head) {
			const h = head.querySelector("h4");
			if (h) h.textContent = "Welcome back";
			const sub = head.querySelector(".page-card-subtitle");
			if (sub) sub.textContent = "Log in to your workspace.";
		}

		// Primary button label ("Continue" -> "Log in"). The trailing arrow is drawn by
		// the stylesheet, not appended here, so this stays one translatable string.
		const sign_in = section.querySelector(".form-login .btn-login[type='submit']");
		if (sign_in) sign_in.textContent = "Log in";
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", brand_login);
	} else {
		brand_login();
	}
})();
